import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { uid } from '@/lib/readiness/util'
import { scopeKey } from '@/lib/readiness/scope'
import type { MigrationAnalyticsEvent, MigrationFacts, MigrationItem, MigrationItemEvent, MigrationItemEventType, MigrationItemStatus, RescanResult, SystemAgility } from '@/api/types/migration'
import { canClose } from '@/lib/migration/engine'
import { emptyMigrationScope, itemCode, type MigrationScope } from '@/lib/migration/scope'

// Scopes persisted against the withdrawn demo tenant, dropped by migrate.
const RETIRED_DEMO_SCOPE_PREFIX = 'demo-org::'

// ── Migration Tracker store ─────────────────────────────────────────────────
// Everything the tracker keeps per org × index, in the browser, persisted
// under `qtz-migration`. The submission plan (Part A, annexes, versions, the
// submission record) moved to qtz-readiness: see lib/migration/usePlan. Record
// shapes mirror the tables the tracker will get so the screens can be pointed
// at the `/migration/items…` endpoints later (see api/clients/migrationApi).

const now = () => new Date().toISOString()

export class CloseGateError extends Error {
  constructor(public failing_asset_ids: string[], public reason: string) {
    super(`Close-out gate failed: ${failing_asset_ids.join(', ') || reason}`)
  }
}

// ── Internal helpers ────────────────────────────────────────────────────────

function itemEvent(s: MigrationScope, itemId: string, type: MigrationItemEventType, actor: string, extra: Partial<MigrationItemEvent> = {}, at = now()): MigrationScope {
  return { ...s, item_events: [...s.item_events, { id: uid('ie'), item_id: itemId, event_type: type, actor, occurred_at: at, from: null, to: null, note: null, ...extra }] }
}

// A scan of the item's scoped assets by the configured scanner. In the
// browser engine the scanner answers from the facts: an asset still carrying
// its vulnerable algorithm reports present; an asset the scanner cannot see
// (a manual row) reports present too, since absence cannot be shown.
function runScanner(s: MigrationScope, assetIds: string[], at: string): RescanResult['outcomes'] {
  const scan_id = uid('scan')
  return assetIds.map((asset_id) => {
    const a = s.facts?.assets.find((x) => x.id === asset_id)
    return { asset_id, vulnerable_present: a ? a.vulnerable_present : true, scan_id, scanned_at: at }
  })
}

interface MigrationStore {
  scopes: Record<string, MigrationScope>
  ensureScope: (orgId: string, indexId: string) => void
  resetScope: (orgId: string, indexId: string) => void
  // Daily readiness sync and on-demand Re-sync (§12): refresh the facts the
  // tracker raises items against.
  sync: (orgId: string, indexId: string, facts: MigrationFacts, actor: string) => void

  // Migration items (§2, §3)
  createItem: (
    orgId: string,
    indexId: string,
    input: { title: string; system_id: string | null; component_id?: string | null; capability?: MigrationItem['capability']; target_source?: MigrationItem['target_source']; step_links?: MigrationItem['step_links']; current_algorithm: string; target_algorithm: string; owner: string | null; target_date: string | null; wave_id: string | null; asset_ids: string[]; created_from_finding_id?: string | null; created_from_group?: boolean; vendor_gated_year?: number | null; notes?: string; status?: MigrationItemStatus },
    actor: string
  ) => MigrationItem
  updateItem: (orgId: string, indexId: string, itemId: string, patch: Partial<Pick<MigrationItem, 'title' | 'target_algorithm' | 'target_date' | 'owner_user_id' | 'wave_id' | 'notes' | 'vendor_gated_year' | 'target_rationale_ref' | 'component_id' | 'capability' | 'target_source' | 'step_links'>>, actor: string) => void
  setItemStatus: (orgId: string, indexId: string, itemId: string, status: MigrationItemStatus, actor: string) => void
  setItemScope: (orgId: string, indexId: string, itemId: string, assetIds: string[], actor: string) => void
  setDependency: (orgId: string, indexId: string, itemId: string, dependsOn: string | null, actor: string) => void
  requestRescan: (orgId: string, indexId: string, itemId: string, actor: string) => RescanResult
  closeItem: (orgId: string, indexId: string, itemId: string, actor: string) => void

  // Agility (§4)
  setAgility: (orgId: string, indexId: string, systemId: string, patch: Partial<Omit<SystemAgility, 'system_id' | 'set_by' | 'set_at'>>, actor: string) => void

  track: (orgId: string, indexId: string, ev: MigrationAnalyticsEvent) => void

  // Catalogue fan-out (I3 §11.2, Note 5): items whose target follows the
  // catalogue move with the vendor date and log `due_set` by `system`.
  applyCatalogueDate: (orgId: string, indexId: string, input: { asset_ids: string[]; capability: string; roadmap_date: string | null }) => MigrationItem[]
}

function withScope(scopes: Record<string, MigrationScope>, orgId: string, indexId: string, fn: (s: MigrationScope) => MigrationScope) {
  const key = scopeKey(orgId, indexId)
  const cur = scopes[key] ?? emptyMigrationScope()
  return { ...scopes, [key]: fn(cur) }
}

const endOfMonth = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}

export const useMigrationStore = create<MigrationStore>()(
  persist(
    (set, get) => ({
      scopes: {},

      ensureScope: (orgId, indexId) => {
        const key = scopeKey(orgId, indexId)
        if (get().scopes[key]) return
        set((st) => ({ scopes: { ...st.scopes, [key]: emptyMigrationScope() } }))
      },

      resetScope: (orgId, indexId) => set((st) => ({ scopes: { ...st.scopes, [scopeKey(orgId, indexId)]: emptyMigrationScope() } })),

      sync: (orgId, indexId, facts) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const at = now()
            const waveChanged = s.facts && facts.wave_plan_updated_at && s.facts.wave_plan_updated_at !== facts.wave_plan_updated_at
            return { ...s, facts, last_sync_at: at, wave_plan_changed_at: waveChanged ? facts.wave_plan_updated_at : s.wave_plan_changed_at }
          }),
        })),

      createItem: (orgId, indexId, input, actor) => {
        const at = now()
        let created!: MigrationItem
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const seq = s.item_seq + 1
            const item: MigrationItem = {
              id: uid('mi'),
              org_id: orgId,
              code: itemCode(seq),
              title: input.title,
              system_id: input.system_id,
              component_id: input.component_id ?? null,
              capability: input.capability ?? null,
              target_source: input.target_source ?? 'owner',
              step_links: input.step_links ?? [],
              current_algorithm: input.current_algorithm,
              target_algorithm: input.target_algorithm,
              target_rationale_ref: null,
              owner_user_id: input.owner,
              target_date: input.target_date,
              wave_id: input.wave_id,
              status: input.status ?? 'not_started',
              created_from_finding_id: input.created_from_finding_id ?? null,
              created_from_group: !!input.created_from_group,
              vendor_gated_year: input.vendor_gated_year ?? null,
              notes: input.notes ?? '',
              created_at: at,
              updated_at: at,
            }
            created = item
            let next: MigrationScope = {
              ...s,
              item_seq: seq,
              items: [...s.items, item],
              item_assets: [...s.item_assets, ...input.asset_ids.map((asset_id) => ({ item_id: item.id, asset_id }))],
              analytics: [...s.analytics, { name: 'migration.item_created', item_id: item.id, from: input.created_from_finding_id ? 'finding' : input.created_from_group ? 'group' : 'blank' }],
            }
            next = itemEvent(next, item.id, 'created', actor, { to: item.status, note: input.created_from_finding_id ? `From Discovery finding ${input.created_from_finding_id}` : input.created_from_group ? 'From CBOM asset group' : null }, at)
            return next
          }),
        }))
        return created
      },

      updateItem: (orgId, indexId, itemId, patch, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const cur = s.items.find((i) => i.id === itemId)
            if (!cur) return s
            const at = now()
            const item: MigrationItem = { ...cur, ...patch, updated_at: at }
            let next: MigrationScope = { ...s, items: s.items.map((i) => (i.id === itemId ? item : i)) }
            if ((patch.target_algorithm !== undefined && patch.target_algorithm !== cur.target_algorithm) || (patch.target_date !== undefined && patch.target_date !== cur.target_date))
              next = itemEvent(next, itemId, 'target_changed', actor, { from: `${cur.target_algorithm} · ${cur.target_date ?? '—'}`, to: `${item.target_algorithm} · ${item.target_date ?? '—'}` }, at)
            if (patch.wave_id !== undefined && patch.wave_id !== cur.wave_id) next = itemEvent(next, itemId, 'wave_changed', actor, { from: cur.wave_id, to: item.wave_id }, at)
            // Same request: the linked annex field follows (§2.2).
            return next
          }),
        })),

      // Any move except verified → closed is free and logged (§2.1); closing
      // runs the gate, and a closed item cannot be reopened (§3.1).
      setItemStatus: (orgId, indexId, itemId, status, actor) => {
        if (status === 'closed') {
          get().closeItem(orgId, indexId, itemId, actor)
          return
        }
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const cur = s.items.find((i) => i.id === itemId)
            if (!cur || cur.status === status || cur.status === 'closed') return s
            const at = now()
            let next: MigrationScope = { ...s, items: s.items.map((i) => (i.id === itemId ? { ...i, status, updated_at: at } : i)), analytics: [...s.analytics, { name: 'migration.item_status_changed', item_id: itemId, from: cur.status, to: status }] }
            next = itemEvent(next, itemId, 'status_changed', actor, { from: cur.status, to: status }, at)
            return next
          }),
        }))
      },

      setItemScope: (orgId, indexId, itemId, assetIds, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const cur = s.items.find((i) => i.id === itemId)
            if (!cur || cur.status === 'closed') return s
            const before = s.item_assets.filter((x) => x.item_id === itemId).map((x) => x.asset_id)
            if (before.length === assetIds.length && before.every((id) => assetIds.includes(id))) return s
            const at = now()
            let next: MigrationScope = { ...s, item_assets: [...s.item_assets.filter((x) => x.item_id !== itemId), ...assetIds.map((asset_id) => ({ item_id: itemId, asset_id }))], items: s.items.map((i) => (i.id === itemId ? { ...i, updated_at: at } : i)) }
            next = itemEvent(next, itemId, 'scope_changed', actor, { from: String(before.length), to: String(assetIds.length) }, at)
            return next
          }),
        })),

      setDependency: (orgId, indexId, itemId, dependsOn, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            if (dependsOn === itemId) return s
            const deps = s.item_deps.filter((d) => d.item_id !== itemId)
            if (dependsOn) deps.push({ item_id: itemId, depends_on_item_id: dependsOn })
            return itemEvent({ ...s, item_deps: deps }, itemId, 'scope_changed', actor, { note: dependsOn ? `Depends on ${s.items.find((i) => i.id === dependsOn)?.code ?? dependsOn}` : 'Dependency removed' })
          }),
        })),

      requestRescan: (orgId, indexId, itemId, actor) => {
        const at = now()
        let result!: RescanResult
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const scoped = s.item_assets.filter((x) => x.item_id === itemId).map((x) => x.asset_id)
            const outcomes = runScanner(s, scoped, at)
            result = { id: uid('rs'), item_id: itemId, scan_id: outcomes[0]?.scan_id ?? uid('scan'), scanner: s.facts?.scanner ?? 'Discovery scan orchestration (M1 F12)', requested_by: actor, requested_at: at, completed_at: at, outcomes }
            let next: MigrationScope = { ...s, rescans: [...s.rescans, result], analytics: [...s.analytics, { name: 'migration.rescan_requested', item_id: itemId }] }
            next = itemEvent(next, itemId, 'rescan_requested', actor, { note: result.scan_id }, at)
            next = itemEvent(next, itemId, 'rescan_result', actor, { note: `${outcomes.filter((o) => o.vulnerable_present).length} of ${outcomes.length} still vulnerable` }, at)
            return next
          }),
        }))
        return result
      },

      closeItem: (orgId, indexId, itemId, actor) => {
        const key = scopeKey(orgId, indexId)
        const s = get().scopes[key]
        const item = s?.items.find((i) => i.id === itemId)
        if (!s || !item) throw new CloseGateError([], 'not_found')
        if (item.status === 'closed') return
        const scoped = s.item_assets.filter((x) => x.item_id === itemId).map((x) => x.asset_id)
        // Server-side gate: the same function the drawer uses (Note 4).
        const gate = canClose(item, scoped, s.item_events, s.rescans)
        if (!gate.ok) throw new CloseGateError(gate.failing_asset_ids, gate.reason ?? 'vulnerable')
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (sc) => {
            const at = now()
            let next: MigrationScope = { ...sc, items: sc.items.map((i) => (i.id === itemId ? { ...i, status: 'closed', updated_at: at } : i)), analytics: [...sc.analytics, { name: 'migration.item_status_changed', item_id: itemId, from: item.status, to: 'closed' }, { name: 'migration.item_closed', item_id: itemId }] }
            next = itemEvent(next, itemId, 'status_changed', actor, { from: item.status, to: 'closed' }, at)
            next = itemEvent(next, itemId, 'closed', actor, { note: gate.scan_ref?.scan_id ?? null }, at)
            return next
          }),
        }))
      },

      setAgility: (orgId, indexId, systemId, patch, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const at = now()
            const cur = s.agility.find((a) => a.system_id === systemId) ?? { system_id: systemId, algorithm_configurable: false, key_rotation_automated: false, library_abstracted: false, note: '', set_by: null, set_at: null }
            const row: SystemAgility = { ...cur, ...patch, set_by: actor, set_at: at }
            return { ...s, agility: [...s.agility.filter((a) => a.system_id !== systemId), row] }
          }),
        })),

      track: (orgId, indexId, ev) => set((st) => ({ scopes: withScope(st.scopes, orgId, indexId, (s) => ({ ...s, analytics: [...s.analytics, ev] })) })),

      applyCatalogueDate: (orgId, indexId, input) => {
        const moved: MigrationItem[] = []
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            if (!input.roadmap_date) return s
            const target = endOfMonth(input.roadmap_date)
            const at = now()
            let next = s
            s.items.forEach((it) => {
              if (it.target_source !== 'catalogue' || it.status === 'closed' || it.capability !== input.capability || it.target_date === target) return
              const scoped = s.item_assets.filter((x) => x.item_id === it.id).map((x) => x.asset_id)
              if (!scoped.some((a) => input.asset_ids.includes(a))) return
              const updated = { ...it, target_date: target, vendor_gated_year: Number(target.slice(0, 4)), updated_at: at }
              moved.push(updated)
              next = { ...next, items: next.items.map((x) => (x.id === it.id ? updated : x)) }
              next = itemEvent(next, it.id, 'due_set', 'system', { from: it.target_date, to: target, note: 'Target follows the catalogue vendor roadmap date' }, at)
            })
            return next
          }),
        }))
        return moved
      },
    }),
    {
      name: 'qtz-migration',
      version: 4,
      // v4: the submission plan moved to qtz-readiness; a persisted scope
      // keeps only its tracker keys. Older scopes are reduced the same way;
      // the demo tenant's scopes are dropped.
      migrate: (persisted) => {
        const st = (persisted ?? { scopes: {} }) as { scopes: Record<string, Partial<MigrationScope> & Record<string, unknown>> }
        const scopes: Record<string, MigrationScope> = {}
        Object.entries(st.scopes ?? {}).forEach(([k, sc]) => {
          if (k.startsWith(RETIRED_DEMO_SCOPE_PREFIX)) return
          const empty = emptyMigrationScope()
          scopes[k] = {
            items: (sc.items ?? empty.items).map((i) => ({ ...i, component_id: i.component_id ?? null, capability: i.capability ?? null, target_source: i.target_source ?? 'owner', step_links: i.step_links ?? [] })),
            item_assets: sc.item_assets ?? empty.item_assets,
            item_deps: sc.item_deps ?? empty.item_deps,
            item_events: sc.item_events ?? empty.item_events,
            rescans: sc.rescans ?? empty.rescans,
            agility: sc.agility ?? empty.agility,
            analytics: sc.analytics ?? empty.analytics,
            facts: sc.facts ? { ...sc.facts, components: sc.facts.components ?? [], assets: sc.facts.assets.map((a) => ({ ...a, component_id: a.component_id ?? null })) } : null,
            item_seq: sc.item_seq ?? 0,
            last_sync_at: sc.last_sync_at ?? null,
            wave_plan_changed_at: sc.wave_plan_changed_at ?? null,
          }
        })
        return { scopes } as unknown as MigrationStore
      },
    }
  )
)

export function useMigrationScope(orgId: string, indexId: string): MigrationScope | null {
  return useMigrationStore((s) => s.scopes[scopeKey(orgId, indexId)] ?? null)
}

export { emptyMigrationScope, itemCode } from '@/lib/migration/scope'
export type { MigrationScope } from '@/lib/migration/scope'
